import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  DocumentVersionFieldFragment,
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalSubmitValidationReviewsInput,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentReviewStatus,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionFolderValidationErrorReviewFragment,
  InternalGetIngestionDocumentFragment,
} from '@client/graphql/__generated__/types';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { DocumentNotFoundState } from '~/common/components/DocumentNotFoundState';
import { FolderNotFoundState } from '~/common/components/FolderNotFoundState';
import { ComplianceDrawerHeader } from './ComplianceDrawerHeader';
import { ComplianceOverview } from './ComplianceOverview';
import { CompliancePDFViewer } from './CompliancePDFViewer';
import { ComplianceSummary } from './ComplianceSummary';
import {
  ComplianceValidationSidebar,
  ComplianceValidationSidebarProps,
} from './ComplianceValidationSidebar';
import { IssueFormData } from './components/AddIssueModal';
import { ComplianceDocumentsSidebar } from './DocumentsSidebar/ComplianceDocumentsSidebar';

export type ComplianceValidationVariant = 'external' | 'internal';

interface BaseComplianceDrawerProps {
  isOpen: boolean;
  onClose: () => void;

  // [NOTE] we pass down selectedIngestionDocumentId for quicker UI updates before selectedIngestionDocument is fetched
  selectedIngestionDocumentId: string | null;
  setSelectedIngestionDocumentId: (selectedDocumentId: string | null) => void;

  folder: ExternalIngestionFolderFragment | null;
  documents: ExternalIngestionDocumentFragment[];
  validationData: ExternalValidationReportDataFragment | null;
  loading: boolean;
  selectedDocumentLoading?: boolean;
  variant: ComplianceValidationVariant;

  // Folder validation error review props
  hiddenFolderIssues?: IngestionFolderValidationErrorReviewFragment[];
  onAddFolderIssue?: (issueData: IssueFormData) => void | Promise<void>;
  onDeleteFolderIssue?: (reviewId: string) => void | Promise<void>;
  onHideFolderIssue?: (
    isHidden: boolean,
    message?: string,
    documentCategory?: string,
    documentForm?: string,
    documentName?: string,
    ruleUuid?: string
  ) => void | Promise<void>;
  userAddedFolderIssues?: IngestionFolderValidationErrorReviewFragment[];

  // Document validation error review props
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  userAddedDocumentIssues?: IngestionDocumentValidationErrorReviewFragment[];
  onHideDocumentValidation?: (
    isHidden: boolean,
    message?: string,
    ingestionDocumentValidationId?: string,
    ruleUuid?: string,
    contractDocumentUuid?: string
  ) => void | Promise<void>;
  onAddDocumentIssue?: (issueData: IssueFormData) => void | Promise<void>;
  onDeleteDocumentIssue?: (reviewId: string) => void | Promise<void>;

  // Document review props
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  currentDocumentReview?: IngestionDocumentReviewFragment | null;
  onCreateOrUpdateDocumentReview?: (
    ingestionDocumentId: string,
    status: IngestionDocumentReviewStatus,
    version?: string,
    reviewId?: string
  ) => void | Promise<void>;

  // Validation review submission props
  onSubmitValidationReviews?: (
    input: Omit<ExternalSubmitValidationReviewsInput, 'ingestionFolderId'>
  ) => void | Promise<void>;

  // Additional actions (e.g., internal-specific buttons)
  additionalActions?: ReactNode;

  // Handle selected document load errors
  selectedDocumentLoadError?: boolean;

  // Handle folder load errors
  folderLoadError?: boolean;
}

interface ExternalComplianceDrawerProps extends BaseComplianceDrawerProps {
  variant: 'external';
  selectedIngestionDocument: GetIngestionDocumentFragment | null;
}

interface InternalComplianceDrawerProps extends BaseComplianceDrawerProps {
  variant: 'internal';
  selectedIngestionDocument: InternalGetIngestionDocumentFragment | null;
}

type ComplianceDrawerProps =
  | ExternalComplianceDrawerProps
  | InternalComplianceDrawerProps;

export const ComplianceDrawer: FC<ComplianceDrawerProps> = (props) => {
  const {
    isOpen,
    onClose,
    selectedIngestionDocument,
    selectedIngestionDocumentId,
    setSelectedIngestionDocumentId,
    folder,
    documents,
    validationData,
    loading,
    selectedDocumentLoading = false,
    variant,
    selectedDocumentLoadError,
    folderLoadError,
    hiddenFolderIssues,
    onAddFolderIssue,
    onDeleteFolderIssue,
    onHideFolderIssue,
    userAddedFolderIssues,
    hiddenDocumentValidationReviews,
    userAddedDocumentIssues,
    onHideDocumentValidation,
    onAddDocumentIssue,
    onDeleteDocumentIssue,
    documentReviews,
    submittedDocumentReviews,
    currentDocumentReview,
    onCreateOrUpdateDocumentReview,
    onSubmitValidationReviews,
    additionalActions,
  } = props;
  // State for field highlighting
  const [activeFieldId, setActiveFieldId] = useState<string>('');
  const [activeMappingKey, setActiveMappingKey] = useState<string | undefined>(
    undefined
  );
  const [selectedContractDocumentId, setSelectedContractDocumentId] = useState<
    string | null
  >(null);

  // State for view switching
  const [isOverviewMode, setIsOverviewMode] = useState(false);
  const [isSummaryMode, setIsSummaryMode] = useState(false);

  // Data is now passed as props from wrapper components
  const currentFolder = folder;

  // Sort documents with contract documents first
  const sortedIngestionDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      // Contract documents come first
      const aHasContract =
        a.contractDocuments && a.contractDocuments.length > 0;
      const bHasContract =
        b.contractDocuments && b.contractDocuments.length > 0;

      if (aHasContract && !bHasContract) return -1;
      if (!aHasContract && bHasContract) return 1;

      // Then sort by name
      return a.documentName.localeCompare(b.documentName);
    });
  }, [documents]);

  // Create a map of repositioned fields from the selected ingestion document
  const validationFieldsMap = useMemo(() => {
    const map = new Map<
      number,
      Array<{
        id: string;
        x: number;
        y: number;
        width: number;
        height: number;
        pageNumber: number;
        contractDocumentUuid: string;
      }>
    >();

    if (!selectedIngestionDocument?.fields) {
      return map;
    }

    // Iterate over the map structure where keys are contract document UUIDs
    Object.entries(selectedIngestionDocument.fields).forEach(
      ([contractDocumentUuid, fields]) => {
        if (Array.isArray(fields)) {
          (fields as unknown as DocumentVersionFieldFragment[]).forEach(
            (repositionedField) => {
              // The repositioned field ID format is typically "{originalId}-repositioned"
              // We need to extract the original field ID
              const originalFieldId = repositionedField?.id?.replace(
                '-repositioned',
                ''
              );
              const pageNumber = repositionedField.pageNumber;

              if (!map.has(pageNumber)) {
                map.set(pageNumber, []);
              }

              map.get(pageNumber)?.push({
                id: originalFieldId, // Keep the original ID for matching with validation clicks
                x: repositionedField.x,
                y: repositionedField.y,
                width: repositionedField.width,
                height: repositionedField.height,
                pageNumber: repositionedField.pageNumber,
                contractDocumentUuid, // Add contract document UUID for filtering
              });
            }
          );
        }
      }
    );

    return map;
  }, [selectedIngestionDocument]);

  const { isOpen: leftSidebarIsOpen, onToggle: toggleLeftSidebar } =
    useDisclosure({
      defaultIsOpen: true,
    });

  const { isOpen: rightSidebarIsOpen, onToggle: toggleRightSidebar } =
    useDisclosure({
      defaultIsOpen: true,
    });

  // Auto-scroll to highlighted field when activeFieldId changes
  useEffect(() => {
    if (activeFieldId && activeFieldId !== '') {
      const fieldId = activeFieldId;

      // Find the field in the validationFieldsMap to get its page number
      let fieldPageNumber: number | undefined;
      for (const [pageNumber, fields] of validationFieldsMap.entries()) {
        const matchingField = fields.find((field) => {
          const fieldMatches = field.id === fieldId;
          // If we have a selectedContractDocumentId, also filter by that
          if (selectedContractDocumentId) {
            return (
              fieldMatches &&
              field.contractDocumentUuid === selectedContractDocumentId
            );
          }

          return fieldMatches;
        });

        if (matchingField) {
          fieldPageNumber = pageNumber;
          break;
        }
      }

      window.requestAnimationFrame(() => {
        // First scroll to the page containing the field
        if (fieldPageNumber !== undefined) {
          const pageElement = document.querySelector(
            `div[data-page-number="${fieldPageNumber}"]`
          );

          pageElement?.scrollIntoView();
        }

        // Then scroll to center the specific field
        const fieldElement = document.querySelector(
          `div[data-preview-field-id="${fieldId}"]`
        );

        fieldElement?.scrollIntoView({ block: 'center' });
      });
    }
  }, [activeFieldId, validationFieldsMap, selectedContractDocumentId]);

  // Handle field click from validation sidebar
  const handleValidationFieldClick = useCallback(
    (mappingKeys: string[], contractDocumentId?: string) => {
      if (!selectedIngestionDocument?.fields) {
        return;
      }

      // Find the current index in the mapping keys array
      const currentIndex = mappingKeys.findIndex(
        (mappingKey) => mappingKey === activeMappingKey
      );

      // Determine which mapping key to try next
      let targetMappingKey: string;
      if (currentIndex === -1) {
        // If no current mapping key is selected, start from the beginning
        targetMappingKey = mappingKeys[0];
      } else if (currentIndex < mappingKeys.length - 1) {
        // Move to the next mapping key
        targetMappingKey = mappingKeys[currentIndex + 1];
      } else {
        // Wrap around to the first mapping key
        targetMappingKey = mappingKeys[0];
      }

      // Find the field with the target mapping key
      let matchingField: DocumentVersionFieldFragment | undefined;

      if (contractDocumentId) {
        const fields = selectedIngestionDocument.fields[
          contractDocumentId
        ] as unknown as DocumentVersionFieldFragment[] | undefined;
        // Try to find the field in the specific contract document
        for (const field of fields || []) {
          if (field.mappingKey === targetMappingKey) {
            matchingField = field;
            break;
          }
        }
      } else {
        // If no contractDocumentId, search across all contract documents
        for (const [, fields] of Object.entries(
          selectedIngestionDocument.fields
        )) {
          if (Array.isArray(fields)) {
            matchingField = (
              fields as unknown as DocumentVersionFieldFragment[]
            ).find((field) => field.mappingKey === targetMappingKey);
            if (matchingField) break;
          }
        }
      }

      // If the target mapping key doesn't exist, try to find the first existing one
      if (!matchingField && currentIndex === -1) {
        for (const mappingKey of mappingKeys) {
          if (contractDocumentId) {
            const fields = selectedIngestionDocument.fields[
              contractDocumentId
            ] as unknown as DocumentVersionFieldFragment[] | undefined;
            for (const field of fields || []) {
              if (field.mappingKey === mappingKey) {
                matchingField = field;
                targetMappingKey = mappingKey;
                break;
              }
            }
          } else {
            for (const [, fields] of Object.entries(
              selectedIngestionDocument.fields
            )) {
              if (Array.isArray(fields)) {
                matchingField = (
                  fields as unknown as DocumentVersionFieldFragment[]
                ).find((field) => field.mappingKey === mappingKey);
                if (matchingField) {
                  targetMappingKey = mappingKey;
                  break;
                }
              }
            }
          }
          if (matchingField) break;
        }
      }

      if (!matchingField) {
        return;
      }

      // Use the original field ID (without -repositioned suffix) for highlighting
      const originalFieldId = matchingField.id.replace('-repositioned', '');

      setSelectedContractDocumentId(contractDocumentId || null);
      setActiveFieldId(originalFieldId);
      setActiveMappingKey(targetMappingKey);
    },
    [selectedIngestionDocument, activeMappingKey]
  );

  const handleClose = useCallback(() => {
    setSelectedIngestionDocumentId(null);
    setActiveFieldId('');
    setActiveMappingKey(undefined);
    setSelectedContractDocumentId(null);
    setIsOverviewMode(false);
    setIsSummaryMode(false);
    onClose();
  }, [onClose, setSelectedIngestionDocumentId]);

  const handleViewAllIssues = useCallback(() => {
    setIsOverviewMode(true);
    setIsSummaryMode(false);
  }, []);

  const handleViewSummary = useCallback(() => {
    setIsSummaryMode(true);
    setIsOverviewMode(false);
  }, []);

  const onDocumentSelect = useCallback(
    (
      document: ExternalIngestionDocumentFragment | null,
      contractDocumentId?: string
    ) => {
      setSelectedIngestionDocumentId(document?.id || null);
      setSelectedContractDocumentId(contractDocumentId || null);
      setIsOverviewMode(false);
      setIsSummaryMode(false);
    },
    [setSelectedIngestionDocumentId]
  );

  // Loading state is passed as prop
  const isLoading = loading;

  return (
    <Drawer
      autoFocus={false}
      isOpen={isOpen}
      placement="right"
      size="full"
      onClose={handleClose}
    >
      <DrawerOverlay />
      <DrawerContent bg="compliance.drawer.bg" maxW="90vw" w="90%">
        <ComplianceDrawerHeader
          additionalActions={additionalActions}
          currentFolder={currentFolder}
          documentReviews={documentReviews}
          documents={sortedIngestionDocuments}
          submittedDocumentReviews={submittedDocumentReviews}
          validationData={validationData}
          variant={variant}
          onClose={handleClose}
          onViewSummary={handleViewSummary}
        />

        <DrawerBody p={0}>
          {isLoading ? (
            <CenterSpinner containerProps={{ height: '100%' }} />
          ) : (
            <Flex h="100%">
              {/* Left Toggle Button */}
              <Box position="relative">
                <IconButton
                  aria-label={
                    leftSidebarIsOpen ? 'Collapse sidebar' : 'Expand sidebar'
                  }
                  border="1px"
                  icon={
                    leftSidebarIsOpen ? (
                      <ChevronLeftIcon />
                    ) : (
                      <ChevronRightIcon />
                    )
                  }
                  left={leftSidebarIsOpen ? -8 : -1}
                  position="absolute"
                  size="sm"
                  top={4}
                  zIndex={10}
                  onClick={toggleLeftSidebar}
                />
              </Box>

              <Collapse in={leftSidebarIsOpen}>
                {currentFolder && (
                  <ComplianceDocumentsSidebar
                    documentReviews={documentReviews}
                    documents={sortedIngestionDocuments}
                    folder={currentFolder}
                    hiddenDocumentValidationReviews={
                      hiddenDocumentValidationReviews
                    }
                    hiddenFolderIssues={hiddenFolderIssues}
                    loading={loading}
                    selectedDocument={selectedIngestionDocument}
                    selectedDocumentId={selectedIngestionDocumentId}
                    submittedDocumentReviews={submittedDocumentReviews}
                    userAddedFolderIssues={userAddedFolderIssues}
                    validationData={validationData}
                    variant={variant}
                    onAddFolderIssue={onAddFolderIssue}
                    onDeleteFolderIssue={onDeleteFolderIssue}
                    onDocumentSelect={onDocumentSelect}
                    onHideFolderIssue={onHideFolderIssue}
                    onViewAllIssues={handleViewAllIssues}
                  />
                )}
              </Collapse>

              <Flex
                direction="column"
                flex={1}
                overflow="hidden"
                position="relative"
              >
                {/* Right Toggle Button */}
                {!isOverviewMode && !isSummaryMode && (
                  <Box position="absolute" right={0} top={4} zIndex={10}>
                    <IconButton
                      aria-label={
                        rightSidebarIsOpen
                          ? 'Collapse validation sidebar'
                          : 'Expand validation sidebar'
                      }
                      border="1px"
                      icon={
                        rightSidebarIsOpen ? (
                          <ChevronRightIcon />
                        ) : (
                          <ChevronLeftIcon />
                        )
                      }
                      position="relative"
                      right={rightSidebarIsOpen ? -8 : -1}
                      size="sm"
                      onClick={toggleRightSidebar}
                    />
                  </Box>
                )}

                <Box flex={1} overflow="hidden">
                  {isSummaryMode ? (
                    currentFolder ? (
                      <ComplianceSummary
                        documentReviews={documentReviews}
                        documents={sortedIngestionDocuments}
                        hiddenDocumentValidationReviews={
                          hiddenDocumentValidationReviews
                        }
                        ingestionFolder={currentFolder}
                        submittedDocumentReviews={submittedDocumentReviews}
                        userAddedDocumentIssues={userAddedDocumentIssues}
                        validationData={validationData}
                        onCreateOrUpdateDocumentReview={
                          onCreateOrUpdateDocumentReview
                        }
                        onSubmitValidationReviews={onSubmitValidationReviews}
                      />
                    ) : folderLoadError ? (
                      <FolderNotFoundState alignTop compact />
                    ) : (
                      <Box p={6}>
                        <Text>Loading folder data...</Text>
                      </Box>
                    )
                  ) : isOverviewMode ? (
                    currentFolder ? (
                      <ComplianceOverview
                        documents={sortedIngestionDocuments}
                        hiddenDocumentValidationReviews={
                          hiddenDocumentValidationReviews
                        }
                        hiddenFolderIssues={hiddenFolderIssues}
                        submittedDocumentReviews={submittedDocumentReviews}
                        userAddedDocumentIssues={userAddedDocumentIssues}
                        userAddedFolderIssues={userAddedFolderIssues}
                        validationData={validationData}
                        onDocumentSelect={onDocumentSelect}
                      />
                    ) : folderLoadError ? (
                      <FolderNotFoundState alignTop compact />
                    ) : (
                      <Box p={6}>
                        <Text>Loading folder data...</Text>
                      </Box>
                    )
                  ) : selectedDocumentLoadError &&
                    !selectedIngestionDocument ? (
                    <DocumentNotFoundState alignTop compact />
                  ) : (
                    <CompliancePDFViewer
                      activeFieldId={activeFieldId}
                      selectedContractDocumentId={selectedContractDocumentId}
                      selectedDocument={selectedIngestionDocument}
                      selectedDocumentLoading={
                        selectedDocumentLoading || !selectedIngestionDocument
                      }
                      validationFieldsMap={validationFieldsMap}
                    />
                  )}
                </Box>
              </Flex>

              {!isOverviewMode && !isSummaryMode && (
                <Collapse in={rightSidebarIsOpen}>
                  <ComplianceValidationSidebar
                    {...({
                      folder: currentFolder,
                      loading,
                      selectedContractDocumentId,
                      selectedDocument: selectedIngestionDocument,
                      validationData,
                      variant,
                      hiddenDocumentValidationReviews,
                      userAddedDocumentIssues,
                      onAddDocumentIssue,
                      onDeleteDocumentIssue,
                      onHideDocumentValidation,
                      onCreateOrUpdateDocumentReview,
                      currentDocumentReview,
                      submittedDocumentReviews,
                      onValidationFieldClick: handleValidationFieldClick,
                      // [NOTE] Cast here to help TypeScript understand the discriminated union relationship
                    } as ComplianceValidationSidebarProps)}
                  />
                </Collapse>
              )}
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
